import * as React from 'react';
import { Spinner } from '@/components/ui/spinner';
import { VerbChip, type SubmittalVerb } from '@/components/ui/verb-chip';
import { useEdlListQuery } from '@/lib/api/deliverables';
import { statusToVerb } from '@/lib/api/deliverables';

/**
 * Submittal-verbs counter card (C20-132 / engagement overview row 1 right).
 *
 * Prototype contract (`screens-engagement.js → window.Screens.overview`,
 * the "Submittal verbs · this engagement" card): six right-aligned rows of
 * [VerbChip] [count], one per submittal verb.
 *
 * Real backend wire-up:
 *   - Source: `GET /api/v1/contract/edl?engagement_id=<id>` (T-0065 / T-0102)
 *   - Aggregate client-side: status → verb (via `statusToVerb`) → count
 *   - `is_overdue` derived flag widens the `overdue` bucket (an entry can
 *     simultaneously be `outstanding` AND overdue per the EDL contract;
 *     the prototype's row breakdown shows overdue separately).
 *
 * The card renders six rows in the prototype's exact verb order:
 *   Approved · Approved as Noted · Revise & Resubmit · Rejected ·
 *   Outstanding · Overdue
 *
 * If the EDL list is empty (no contract baseline yet), the card surfaces an
 * explicit "Awaiting contract baseline" message rather than rendering all-
 * zeros (which would mislead the reader into thinking the engagement has no
 * activity vs. has no contract).
 */
export interface SubmittalVerbsCardProps {
  engagementId: string;
}

const VERB_ORDER: SubmittalVerb[] = [
  'approved',
  'approved_as_noted',
  'revise',
  'rejected',
  'outstanding',
  'overdue',
];

export function SubmittalVerbsCard({ engagementId }: SubmittalVerbsCardProps) {
  // Cap the list fetch — the overview only needs counts. The EDL endpoint
  // doesn't expose a `count_only=true` mode (no backend ticket for it),
  // so we pull the first page (limit 200, server cap) and aggregate.
  const edlQuery = useEdlListQuery(engagementId, { limit: 500, item_type: 'deliverable' });

  const counts = React.useMemo(() => {
    const out: Record<SubmittalVerb, number> = {
      approved: 0,
      approved_as_noted: 0,
      revise: 0,
      rejected: 0,
      outstanding: 0,
      overdue: 0,
      submitted: 0,
    };
    // The EDL endpoint also returns milestones, payment terms, KPIs, SLAs
    // and integrations (they share the register). Submittal verbs describe
    // DELIVERABLE review states only, so everything else is excluded.
    const entries = (edlQuery.data?.entries ?? []).filter((e) => e.item_type === 'deliverable');
    for (const e of entries) {
      const verb = statusToVerb(e.status);
      if (verb && verb !== 'submitted') {
        out[verb] += 1;
      }
      // Independently bump `overdue` for any entry the server flagged
      // overdue — mirrors the prototype's "Overdue" row, which sums every
      // late submittal across all lifecycle states.
      if (e.is_overdue) {
        out.overdue += 1;
      }
    }
    return out;
  }, [edlQuery.data]);

  return (
    <section
      className="rounded-card border border-ink-100 bg-paper-0 p-card-pad"
      aria-labelledby="submittal-verbs-card-title"
    >
      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 id="submittal-verbs-card-title" className="t-h3">
          Submittal verbs · this engagement
        </h2>
      </div>

      {edlQuery.isLoading ? (
        <div className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner /> Loading submittals…
        </div>
      ) : edlQuery.error ? (
        <p className="text-body-sm text-posture-critical">
          Failed to load submittals — {String(edlQuery.error)}
        </p>
      ) : (edlQuery.data?.entries ?? []).filter((e) => e.item_type === 'deliverable').length ===
        0 ? (
        <p className="text-body-sm text-ink-500">
          Awaiting contract baseline — submittal counts appear once the SOW is uploaded and the
          deliverable list is generated.
        </p>
      ) : (
        <ul className="flex flex-col gap-2" role="list">
          {VERB_ORDER.map((verb) => (
            <li key={verb} className="flex items-center justify-between gap-3">
              <VerbChip verb={verb} />
              <span className="font-mono text-body-sm font-medium tabular-nums text-ink-700">
                {counts[verb]}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
